import React, { useState, useEffect } from "react";
import { Card, List, Avatar, Badge, Typography, Spin, Popover } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { Info } from "@mui/icons-material";
import styled from "styled-components";
import { CALL_FEATCH_API, CALL_API } from "common/API";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";

const { Text, Title } = Typography;

const Wrapper = styled(Card)`
	
  .ant-card-body {
    padding: 16px 20px;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .view-all {
    color: #111827;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    text-decoration: underline;
  }
`;

const ListWrapper = styled.div`
  max-height: 340px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 4px;
  }
`;

const AnnouncementItem = styled(List.Item)`
  padding: 10px 4px !important;
  border-bottom: none !important;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
    border-radius: 8px;
  }

  .ant-list-item-meta-title {
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    margin-bottom: 2px !important;
  }

  .time {
    color: #6b7280;
    font-size: 13px;
  }
`;

const ItemAction = styled.div`
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
  ${AnnouncementItem}:hover & {
    opacity: 1;
  }
`;

const formatDate = (isoString) => {
	if (!isoString) return "";
	const date = new Date(isoString);
	const day = date.getDate().toString().padStart(2, "0");
	const month = date.toLocaleDateString("en-US", { month: "short" });
	const year = date.getFullYear();
	const time = date.toLocaleTimeString("en-US", {
		hour: "2-digit",
		minute: "2-digit",
	});
	return `${day}th ${month}, ${year} ; ${time}`;
};

// ── Colors used to fill in "initial" avatars when a user has no profile pic ──
const INITIAL_COLORS = [
	"#f56a00",
	"#7265e6",
	"#ffbf00",
	"#00a2ae",
	"#2D53DA",
	"#ef4444",
];

const getInitialColor = (seed) => {
	const str = String(seed || "");
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	return INITIAL_COLORS[Math.abs(hash) % INITIAL_COLORS.length];
};

const AnnouncementList = () => {
	const [announcements, setAnnouncements] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [viewedIds, setViewedIds] = useState([]);

	const userInfo = useSelector(getUserInfo);
	const userList = useSelector(getUserList);
	const currentUser = userList?.find(
		(u) => String(u.userid) === String(userInfo?.userid)
	);
	const userStartDate = currentUser?.startdate ? new Date(currentUser.startdate) : null;

	const filteredAnnouncements = announcements?.filter((a) => {
		if (String(a?.createdby) === String(userInfo?.userid)) return false;

		// Hide announcements created before the user's start date
		if (userStartDate && a?.createdat) {
			const announcementDate = new Date(a.createdat);
			if (announcementDate < userStartDate) return false;
		}
		// Role filter
		let announcementRoleIds = a?.roleid || a?.roleId || [];
		if (typeof announcementRoleIds === "string") {
			try { announcementRoleIds = JSON.parse(announcementRoleIds); } catch (e) { announcementRoleIds = []; }
		}
		if (Array.isArray(announcementRoleIds) && announcementRoleIds?.length > 0) {
			const userRoles = (userInfo?.role_id || []).map(String);
			const hasAccess = announcementRoleIds.map(String).some((id) => userRoles.includes(id));
			if (!hasAccess) return false;
		}

		// Location filter
		let announcementLocationIds = a?.locationid || a?.locationId || [];
		if (typeof announcementLocationIds === "string") {
			try { announcementLocationIds = JSON.parse(announcementLocationIds); } catch (e) { announcementLocationIds = []; }
		}
		if (Array.isArray(announcementLocationIds) && announcementLocationIds?.length > 0) {
			const userBaseLocationId = userList?.find(
				(u) => String(u.userid) === String(userInfo?.userid)
			)?.baselocationid;
			const normalizedUserLocationId = Array.isArray(userBaseLocationId)
				? userBaseLocationId.map(String)
				: [String(userBaseLocationId || "")];
			const hasLocationAccess = announcementLocationIds
				.map(String)
				.some((id) => normalizedUserLocationId.includes(id));
			if (!hasLocationAccess) return false;
		}

		return true;
	});

	const unreadCount = filteredAnnouncements?.filter((a) => {
		const alreadyViewed =
			a.vieweduserid?.includes(userInfo?.userid) || viewedIds.includes(a.id);
		return !alreadyViewed;
	})?.length;

	const history = useHistory();

	useEffect(() => {
		fetchAnnouncements();
	}, []);

	const fetchAnnouncements = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await CALL_FEATCH_API(
				`insyts-user-dashboard/getannouncement`,
				"get"
			);
			if (response?.status === "SUCCESS") {
				const data = response?.response?.data ?? [];
				setAnnouncements(data);
			} else {
				throw new Error("API returned non-success status");
			}
		} catch (err) {
			console.error("Failed to fetch announcements:", err);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const markAsViewed = async (item) => {
		if (item?.viewstatus || viewedIds.includes(item?.id)) return;
		setViewedIds((prev) => [...prev, item?.id]);
		try {
			await CALL_API(
				"insyts-user-dashboard/updateannouncement",
				"patch",
				{ id: item?.id, viewStatus: true, viewedUserId: userInfo?.userid }
			);
		} catch (err) {
			console.error("Failed to mark announcement as viewed:", err);
			setViewedIds((prev) => prev.filter((id) => id !== item?.id));
		}
	};

	const handleItemClick = (item) => {
		markAsViewed(item);
		const isImportant = item?.level === "Important";
		history.push(
			`/announcement-details/${item?.id}`,
			isImportant ? { viewOnly: true } : {}
		);
	};

	const handleViewAll = () => {
		history.push("/announcement");
	};

	// ── Avatar initial is derived from the announcement's creator (employeename), NOT the title ──
	const getAvatarInfo = (item) => {
		const matchedUser = userList?.find(
			(u) => String(u.userid) === String(item?.createdby)
		);

		const rawName =
			item?.employeename ??
			matchedUser?.name ??
			matchedUser?.username ??
			matchedUser?.employeename ??
			item?.createdbyname ??
			"";

		const name = String(rawName || "").trim();
		const initial = name ? name.charAt(0).toUpperCase() : "?";

		return {
			src: matchedUser?.profileurl || null,
			initial,
			color: getInitialColor(item?.createdby || name),
		};
	};

	return (
		<>
			{/* Header */}
			<div
				className="header"
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<div style={{ display: "flex", alignItems: "center" }}>
					<Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
						Announcement
					</Title>
					<Badge
						count={unreadCount}
						style={{ backgroundColor: "#ef4444", marginLeft: 6 }}
					/>
				</div>

				<p className="view-all" style={{
					color: "#57595c",
					fontSize: "13px",
					cursor: "pointer",
					textDecoration: "underline",
				}} onClick={() => handleViewAll()}>
					View All
				</p>
			</div>

			{/* Content */}
			{loading ? (
				<div style={{ textAlign: "center", padding: "32px 0" }}>
					<Spin />
				</div>
			) : error ? (
				<div style={{ textAlign: "center", padding: "16px 0" }}>
					<Text type="danger">Failed to load: {error}</Text>
				</div>
			) : filteredAnnouncements?.length === 0 ? (
				<div style={{ textAlign: "center", color: "#6b7280", padding: "16px 0" }}>
					<Text>No announcements found.</Text>
				</div>
			) : (
				<ListWrapper>
					<List
						itemLayout="horizontal"
						dataSource={filteredAnnouncements}
						renderItem={(item) => {
							const isImportant = item?.level === "Important";
							const isViewed =
								item?.vieweduserid?.includes(userInfo?.userid) || viewedIds.includes(item?.id);
							const { src, initial, color } = getAvatarInfo(item);

							return (
								<AnnouncementItem
									onClick={() => handleItemClick(item)}
									actions={[
										<ItemAction
											onClick={(e) => {
												e.stopPropagation();
												handleItemClick(item);
											}}
										>
											{isImportant ? (
												<Popover
													content="This is an important announcement and cannot be removed from the list."
													overlayStyle={{ maxWidth: 200 }}
													placement="bottom"
												>
													<Info
														sx={{
															color: "#2D53DA",
															fontSize: "18px",
															cursor: "pointer",
														}}
													/>
												</Popover>
											) : (
												<CloseOutlined
													style={{
														color: "#57595c",
														fontSize: "13px",
														cursor: "pointer",
													}}
												/>
											)}
										</ItemAction>,
									]}
								>
									<List.Item.Meta
										avatar={
											<Badge
												dot={!isViewed}
												offset={[-4, 4]}
												color="#ef4444"
											>
												{src ? (
													<Avatar
														src={src}
														size={46}
														style={{ border: "2px solid #f3f4f6" }}
													/>
												) : (
													<Avatar
														size={46}
														style={{
															border: "2px solid #f3f4f6",
															backgroundColor: "#DEEFEE",
															color: "#00685F",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															fontWeight: 600,
														}}
													>
														{initial}
													</Avatar>
												)}
											</Badge>
										}
										title={
											<span
												style={{
													display: "block",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													maxWidth: "calc(100% - 40px)",
												}}
												title={item.title}
											>
												{item.title}
											</span>
										}
										description={
											<span className="time">
												{formatDate(item.createdat)}
											</span>
										}
									/>
								</AnnouncementItem>
							);
						}}
					/>
				</ListWrapper>
			)}
		</>
	);
};

export default AnnouncementList;